import { auth } from './firebase';
import { functionUrl } from './functions';
import { BUILD_GIT_SHA, BUILD_ID } from './buildInfo';
import { clientEnvironment } from 'shared/platform/environment';

/**
 * Client logging. Mirrors aligned's GoogleCloudLoggerService: batches go to the
 * existing `writeClientLog` Cloud Function, which writes to Cloud Logging under
 * the `flutter-client` log name with pass-through labels.
 *
 * Two project conventions baked in here:
 *  1. Every entry carries `client: "react"` so React logs are separable from the
 *     Flutter app's in the shared project (see docs/03-gcp-firebase-setup.md).
 *  2. Remote logging is skipped in development — log to the console instead —
 *     matching the Flutter logger's dev behavior.
 *
 * Error entries are additionally shaped as a Cloud Error Reporting
 * `ReportedErrorEvent` so they show up in the Error Reporting console with
 * grouping, occurrence counts, and affected-user counts — see
 * `errorReportingPayload` below for what that shape requires, and why only
 * ERROR-and-above entries carry its ingestion marker.
 *
 * Best-effort: a logging failure must never throw into app code, and never
 * recurses back through the logger.
 */

export type LogSeverity =
  | 'DEBUG' | 'INFO' | 'NOTICE' | 'WARNING' | 'ERROR' | 'CRITICAL';

export interface LogErrorOptions {
  context?: string;
  metadata?: Record<string, unknown>;
  user?: { uid?: string; email?: string | null };
  severity?: LogSeverity;
}

const IS_DEV = !clientEnvironment.isProduction;
const CLIENT_LABEL = 'react';
const SERVICE_NAME = 'aligned-beta-frontend';
const SERVICE_VERSION = BUILD_GIT_SHA || BUILD_ID || 'unknown';
/** Marks a log payload for Cloud Error Reporting's log-based ingestion. */
const REPORTED_ERROR_EVENT_TYPE =
  'type.googleapis.com/google.devtools.clouderrorreporting.v1beta1.ReportedErrorEvent';

/**
 * Send a structured informational event using the same best-effort logging
 * boundary as errors. Build lifecycle events use this to make stale-build
 * recovery observable without allowing logging failures to affect the app.
 */
export async function logEventAsync(
  severity: LogSeverity,
  message: string,
  context?: Record<string, unknown>,
): Promise<void> {
  if (IS_DEV) return;
  await sendToCloudLogging({
    severity,
    lines: [message],
    labels: {
      client: CLIENT_LABEL,
      event: message,
    },
    payload: {
      message,
      frontendContext: context,
      serviceContext: { service: SERVICE_NAME, version: SERVICE_VERSION },
      buildId: BUILD_ID,
      gitSha: BUILD_GIT_SHA,
    },
  });
}

/**
 * The `message` Error Reporting parses for the exception and its frames.
 *
 * V8 (Chrome, Edge) already prefixes `error.stack` with `Name: message`, so
 * using it verbatim avoids a duplicated header. Firefox and Safari return bare
 * frames, so the header is prepended there.
 */
export function reportableMessage(message: string, stack: string | undefined): string {
  if (!stack) return message;
  return stack.startsWith(message) ? stack : `${message}\n${stack}`;
}

/**
 * Payload shaped as a Cloud Error Reporting `ReportedErrorEvent`.
 *
 * Log-based ingestion needs three things and silently ignores the entry without
 * them: the `@type` marker, a `serviceContext.service`, and a `message` that
 * carries the stack trace. Everything from `stackTrace` down is ignored by
 * ingestion and exists for the error bot (`scripts/gcp-error-bot`), which reads
 * Cloud Logging directly.
 *
 * The `@type` marker is attached only at ERROR and above. Ingestion honours the
 * marker at *any* severity, so a marked WARNING still files into the Error
 * Reporting console — which put every `defaultSeverity` downgrade (connectivity
 * noise, expired auth) right back on the defect list the downgrade existed to
 * keep it off. Sub-ERROR entries keep the rest of the shape so the error bot's
 * warning trends read unchanged.
 *
 * `errorContext` is deliberately not named `context`: that key belongs to Error
 * Reporting's structured context, which has to be an object.
 */
function errorReportingPayload(
  message: string,
  stack: string | undefined,
  options: LogErrorOptions,
  severity: LogSeverity,
): Record<string, unknown> {
  return {
    ...(severity === 'ERROR' || severity === 'CRITICAL'
      ? { '@type': REPORTED_ERROR_EVENT_TYPE }
      : {}),
    message: reportableMessage(message, stack),
    serviceContext: { service: SERVICE_NAME, version: SERVICE_VERSION },
    context: {
      httpRequest: {
        method: 'GET',
        url: typeof window === 'undefined' ? '' : window.location.href,
        userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
      },
      // Drives Error Reporting's affected-user count.
      user: options.user?.uid ?? 'anonymous',
    },
    stackTrace: stack,
    errorContext: options.context,
    frontendContext: options.metadata,
    uid: options.user?.uid,
    email: options.user?.email ?? undefined,
    buildId: BUILD_ID,
    gitSha: BUILD_GIT_SHA,
  };
}

export function logError(error: unknown, options: LogErrorOptions = {}): void {
  const severity = options.severity ?? defaultSeverity(error);
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  if (IS_DEV) {
    const contextSuffix = options.context ? ` (${options.context})` : '';
    console.error('[%s]%s', severity, contextSuffix, error, options.metadata ?? '');
    return;
  }

  void sendToCloudLogging({
    severity,
    lines: [message],
    stack_trace: stack,
    labels: {
      client: CLIENT_LABEL,
      ...(options.context ? { context: options.context } : {}),
    },
    payload: errorReportingPayload(message, stack, options, severity),
  });
}

/**
 * A `TypeError` by name as well as by prototype: one thrown in another realm
 * (an iframe) fails `instanceof` here, and `errorReporter` rebuilds such a
 * rejection as a plain `Error` that keeps the original name.
 */
function isTypeError(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof Error && error.name === 'TypeError');
}

function defaultSeverity(error: unknown): LogSeverity {
  // Auth/network errors are expected (logout, connectivity loss) — downgrade.
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  if (msg.includes('auth') || msg.includes('network') || msg.includes('offline')) {
    return 'WARNING';
  }
  // `fetch` reports every transport-level failure — offline, DNS, CORS, a
  // request cancelled by an extension — as one of these two fixed TypeError
  // messages, with no keyword the check above would catch: Chromium/Firefox
  // say "Failed to fetch", WebKit says "Load failed". It is connectivity, not
  // a defect, so it belongs with the other expected failures. Matched
  // exactly, and only on TypeError, so an application error that merely
  // mentions fetching still reports as ERROR.
  if (isTypeError(error) && (msg === 'failed to fetch' || msg === 'load failed')) {
    return 'WARNING';
  }
  return 'ERROR';
}

interface CloudLogBody {
  severity: LogSeverity;
  lines: string[];
  stack_trace?: string;
  labels: Record<string, string>;
  payload: Record<string, unknown>;
}

/**
 * The route the entry was produced on, as a Cloud Logging label.
 *
 * The error bot reads `labels.route` (`scripts/gcp-error-bot/signals.js`) to
 * report which screens a signal came from; without it every signal reads
 * "Route(s): none recorded" and an unmappable minified stack has nothing left
 * to locate it by. Path only — query strings and hashes can carry identifiers
 * that have no place in a log label.
 */
function currentRoute(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.pathname || undefined;
}

async function sendToCloudLogging(body: CloudLogBody): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user) return; // writeClientLog requires auth; drop pre-sign-in errors.
    const token = await user.getIdToken();
    const route = currentRoute();
    await fetch(functionUrl('writeClientLog'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...body,
        labels: { ...body.labels, ...(route ? { route } : {}) },
      }),
    });
  } catch {
    // Never let logging failures surface — and never route them back through
    // logError (that would recurse).
  }
}
